import axios from 'axios'
import {Store} from 'vuex'
import {TypeDonnee} from '@/shared/dossier/enum-type-donnee'
import VueI18n from 'vue-i18n'
import Vue from 'vue'

export default class DossierService {
  constructor(private store: Store<any>, private i18n: VueI18n) {
    this.store = store
    this.i18n = i18n
  }

  public getSynoptique(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.SYNOPTIQUE, 'dossier/setSynoptique', businessKey)
  }

  public getMicrofilms(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.MICROFILMS, 'dossier/setMicrofilms', businessKey)
  }

  public getStatistiques(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.STATISTIQUES, 'dossier/setStatistiques', businessKey)
  }

  public getSyntheses(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.SYNTHESES, 'dossier/setSyntheses', businessKey)
  }

  public getRemarques(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.REMARQUES, 'dossier/setRemarques', businessKey)
  }

  public getSuivis(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.SUIVIS, 'dossier/setSuivis', businessKey)
  }

  public getLiens(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.LIENS, 'dossier/setLiens', businessKey)
  }

  public getAffairesLiees(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.AFFAIRES_LIEES, 'dossier/setAffairesLiees', businessKey)
  }

  public getPieces(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.PIECES, 'dossier/setPieces', businessKey)
  }

  public getRelationsProjet(businessKey, isLiee = false): Promise<any> {
    this.store.commit('dossier/addLoadingData', TypeDonnee.RELATIONS_PROJET)

    return this.getAllRelationsProjet(businessKey, isLiee).then(() =>
      this.store.commit('dossier/removeLoadingData', TypeDonnee.RELATIONS_PROJET)
    )
  }

  private getAllRelationsProjet(businessKey, isLiee = false): Promise<any> {
    const typeDonnee = `${TypeDonnee.RELATIONS_PROJET}${isLiee ? '/liee' : ''}`

    return this.chargerDonneesAdditionnelles(typeDonnee, 'dossier/setRelationsProjet', businessKey, false).then((data) => {
      if (data && data.some((rel) => rel.niveauRelation === 0)) {
        data.forEach((rel) =>
          this.getRelationProjetEnfants(`${rel.typeDossierLie}/${rel.numeroDossierLie}/${rel.numeroComplementDossierlie}`, rel.idDossierLie)
        )
      } else if (isLiee) {
        const relationsParente = data && data[0]

        if (!relationsParente) {
          return []
        }

        return this.getAllRelationsProjet(
          `${relationsParente.typeDossier}/${relationsParente.numeroDossier}/${relationsParente.numeroComplementaireDossier}`,
          false
        )
      } else {
        return this.getAllRelationsProjet(businessKey, true)
      }
    })
  }

  public getEtapesAcDematFromSuivi(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.ETAPES_FROM_SUIVI, 'dossier/setEtapes', businessKey)
  }

  public getEtapesAcDemat(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.ETAPES_ACDEMAT, 'dossier/setEtapes', businessKey)
  }

  public getDepotOAC(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_DEPOT, 'dossier/setDepot', businessKey)
  }

  public getCirculationsOAC(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_CIRCULATIONS, 'dossier/setCirculations', businessKey)
  }

  public getSynthesesOac(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_SYNTHESES, 'dossier/setSynthesesOac', businessKey)
  }

  public getDecisions(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_DECISIONS, 'dossier/setDecisions', businessKey)
  }

  public getMiseEnService(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_MISE_EN_SERVICE, 'dossier/setMiseEnService', businessKey)
  }

  public getChantiers(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_CHANTIERS, 'dossier/setChantiers', businessKey)
  }

  public getProlongations(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_PROLONGATIONS, 'dossier/setProlongations', businessKey)
  }

  public getRecours(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_RECOURS, 'dossier/setRecours', businessKey)
  }

  public getEnquetesPubliques(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_ENQUETES_PUBLIQUES, 'dossier/setEnquetesPubliques', businessKey)
  }

  public getObservations(businessKey): Promise<boolean> {
    return this.chargerDonneesAdditionnelles(TypeDonnee.OAC_OBSERVATIONS, 'dossier/setObservations', businessKey)
  }

  chargerDonneesAdditionnelles(typeDonnee, nomMutationDonnees, businessKey, manageLoading = true): Promise<any> {
    if (manageLoading) {
      this.store.commit('dossier/addLoadingData', typeDonnee)
    }
    return axios
      .get(`api/${typeDonnee}`, {
        params: {businessKey}
      })
      .then(({data}) => {
        this.store.commit(nomMutationDonnees, data)
        if (manageLoading) {
          this.store.commit('dossier/removeLoadingData', typeDonnee)
        }
        return data
      })
      .catch(() => {
        if (manageLoading) {
          this.store.commit('dossier/removeLoadingData', typeDonnee)
        }
        Vue.prototype.$edgToast.showError({
          title: this.i18n.t('erreur.titre'),
          message: this.i18n.t('erreur.messageRecuperationDonneesAdditionnelles', {type: typeDonnee})
        })
        return null
      })
  }

  getRelationProjetEnfants(businessKey, idDossierLie) {
    return axios
      .get('api/relationsProjet', {
        params: {businessKey}
      })
      .then(({data}) => {
        this.store.commit('dossier/addRelationsProjet', {relations: data, idDossierLie})
      })
      .catch(() => {
        this.store.commit('dossier/addRelationsProjet', {relations: [], idDossierLie})
      })
  }

  rechercherDossiers(criteres) {
    return axios.post('api/dossier/rechercher', criteres).then(({data}) => {
      return data
    })
  }

  getNombreDossiers(criteres) {
    return axios.post('api/dossier/rechercher/count', criteres).then(({data}) => {
      return data
    })
  }
}
